// Version history (spec §12, phase 6): versions recorded from the op log,
// with who changed what, a rendered preview, and restore.
import type { BoardInfo, VersionInfo } from '../../shared/api.ts';
import type { BoardObject } from '../../shared/types.ts';
import { get, post } from '../api.ts';
import type { Editor } from '../editor.ts';
import { renderThumbnail } from '../export/scene.ts';
import { canvasMeasure, TextLayouts } from '../text/placement.ts';
import { h } from '../util/dom.ts';

function when(ts: number): string {
  const d = new Date(ts);
  const time = d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  const today = new Date();
  const yesterday = new Date(Date.now() - 86_400_000);
  if (d.toDateString() === today.toDateString()) return `Today, ${time}`;
  if (d.toDateString() === yesterday.toDateString()) return `Yesterday, ${time}`;
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

const people = (v: VersionInfo) => {
  const names = v.authors.map((a) => a.name);
  return names.length <= 2 ? names.join(' and ') : `${names.slice(0, 2).join(', ')} and ${names.length - 2} more`;
};

export function createVersionsPanel(ed: Editor, board: () => BoardInfo, notify: (message: string) => void): { el: HTMLElement; open(): void } {
  const list = h('ul', { class: 'version-list', role: 'list' });
  const preview = h('canvas', { class: 'version-preview', 'aria-label': 'Preview of the selected version' });
  const detailText = h('p', { class: 'muted' });
  const restore = h('button', { type: 'button', class: 'btn primary' }, ['Restore this version']);
  const detail = h('div', { class: 'version-detail', hidden: '' }, [preview, detailText, restore]);
  const close = h('button', { type: 'button', class: 'tb-btn', 'aria-label': 'Close version history', title: 'Close' }, ['×']);
  const el = h('aside', { class: 'panel versions', 'aria-label': 'Version history', hidden: '' }, [
    h('div', { class: 'versions-head' }, [h('h2', {}, ['Version history']), close]),
    detail,
    list,
  ]);
  const layouts = new TextLayouts(canvasMeasure, () => ed.people); // separate from the live board's cache
  let chosen: VersionInfo | null = null;

  close.addEventListener('click', () => { el.hidden = true; });
  restore.addEventListener('click', async () => {
    if (!chosen || !confirm(`Restore the board to how it was at ${when(chosen.createdAt)}? Everyone on the board will see the change, and the current state is kept as a version.`)) return;
    restore.disabled = true;
    try {
      await post(`/api/boards/${board().id}/versions/${chosen.seq}/restore`);
      notify(`Restored the version from ${when(chosen.createdAt)}.`);
      await load();
    } catch (err) {
      notify((err as Error).message);
    } finally {
      restore.disabled = false;
    }
  });

  async function load(): Promise<void> {
    list.replaceChildren(h('li', { class: 'muted' }, ['Loading…']));
    detail.hidden = true;
    chosen = null;
    const versions = await get<VersionInfo[]>(`/api/boards/${board().id}/versions`);
    if (!versions.length) {
      list.replaceChildren(h('li', { class: 'muted' }, ['No versions yet. They are recorded automatically as the board changes.']));
      return;
    }
    list.replaceChildren(...versions.map((v, i) => {
      const item = h('button', { type: 'button', class: 'version-item' }, [
        h('strong', {}, [i === 0 ? `${when(v.createdAt)} · latest` : when(v.createdAt)]),
        h('span', { class: 'muted' }, [v.authors.length ? `${people(v)} · ${v.opCount} ${v.opCount === 1 ? 'change' : 'changes'}` : 'Starting point']),
      ]);
      item.addEventListener('click', () => void select(v, item));
      return h('li', {}, [item]);
    }));
  }

  async function select(v: VersionInfo, item: HTMLElement): Promise<void> {
    for (const b of list.querySelectorAll('.version-item')) b.classList.toggle('active', b === item);
    chosen = v;
    detail.hidden = false;
    detailText.textContent = v.authors.map((a) => `${a.name}: ${a.ops} ${a.ops === 1 ? 'change' : 'changes'}`).join(' · ') || 'No changes recorded.';
    restore.hidden = !board().canEdit;
    const { objects } = await get<{ objects: BoardObject[] }>(`/api/boards/${board().id}/versions/${v.seq}`);
    if (chosen !== v) return;
    const byId = new Map(objects.map((o) => [o.id, o]));
    renderThumbnail(preview, objects, { get: (id) => byId.get(id), layouts, images: ed.images }, '#F7F7F5');
  }

  return {
    el,
    open() {
      el.hidden = false;
      void load().catch((err: unknown) => list.replaceChildren(h('li', { class: 'form-error' }, [(err as Error).message])));
    },
  };
}
